import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from '@/lib/slugUtils';
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import Breadcrumb from '@/components/Breadcrumb';
import PageSpecificFAQStructuredData from '@/components/PageSpecificFAQStructuredData';
import { 
  ArrowRight,
  CheckCircle2,
  Zap,
  Award,
  Share2,
  Users,
  Shield
} from 'lucide-react';

const whyPriceFormsIcons = [Zap, Award, Share2, Users, Shield];

export default function CalculatorMistakes() {
  const { t } = useTranslation('calculator-mistakes');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleCTAClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };

  return (
    <>
      <SEOHead
        title={t('meta.title')}
        description={t('meta.description')}
        lang={currentLang}
        path={generateLocalizedUrl('calculator-mistakes', currentLang)}
      />
      
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <Breadcrumb />

          {/* Hero Section */}
          <section className="text-center mb-16 mt-12">
            <Badge variant="secondary" className="mb-6 text-base px-6 py-2" data-testid="badge-hero">
              {t('hero.badge')}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" data-testid="title-hero">
              {t('hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 font-medium" data-testid="text-subtitle">
              {t('hero.subtitle')}
            </p>
          </section>

          {/* Introduction Section */}
          <section className="mb-20">
            <Card className="border-l-4 border-primary">
              <CardContent className="p-6">
                <div className="space-y-4 text-lg">
                  <p data-testid="text-intro-1">{t('intro.paragraph1')}</p>
                  <p data-testid="text-intro-2">{t('intro.paragraph2')}</p>
                  <p data-testid="text-intro-3">{t('intro.paragraph3')}</p>
                  <p data-testid="text-intro-4">{t('intro.paragraph4')}</p>
                  <p className="font-semibold" data-testid="text-intro-5">{t('intro.paragraph5')}</p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Mistakes Section */}
          <section className="mb-20">
            <div className="space-y-10">
              {Array.isArray(t('mistakes', { returnObjects: true })) && (t('mistakes', { returnObjects: true }) as any[]).map((mistake: any, index: number) => (
                <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`card-mistake-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-5xl" data-testid={`icon-mistake-${index}`}>{mistake.icon}</div>
                      <div className="flex-1">
                        <h2 className="text-2xl md:text-3xl font-bold mb-3" data-testid={`title-mistake-${index}`}>
                          {mistake.title}
                        </h2>
                        <p className="text-lg text-muted-foreground mb-4" data-testid={`problem-mistake-${index}`}>
                          {mistake.problem}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 mb-4 border-l-4 border-green-500">
                      <h3 className="font-semibold text-lg mb-3 text-green-800 dark:text-green-200" data-testid={`solution-title-${index}`}>
                        {mistake.solutionTitle}
                      </h3>
                      
                      <div className="space-y-2">
                        {Array.isArray(mistake.solutions) && mistake.solutions.map((solution: string, sIndex: number) => (
                          <div key={sIndex} className="flex items-start gap-2" data-testid={`solution-item-${index}-${sIndex}`}>
                            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            <span className="text-base">{solution}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {mistake.tip && (
                      <div className="bg-primary/5 border-l-4 border-primary p-4 rounded">
                        <p className="text-sm md:text-base" data-testid={`tip-mistake-${index}`}>
                          {mistake.tip}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Bonus Section */}
          <section className="mb-20">
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4" data-testid="icon-bonus">{t('bonus.icon')}</div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6" data-testid="title-bonus">
                    {t('bonus.title')}
                  </h2>
                </div>
                
                <div className="space-y-4 mb-6">
                  {Array.isArray(t('bonus.features', { returnObjects: true })) && (t('bonus.features', { returnObjects: true }) as string[]).map((feature, index) => (
                    <div key={index} className="flex items-start gap-3" data-testid={`bonus-feature-${index}`}>
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-lg font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <p className="text-center text-lg font-semibold bg-primary/10 p-4 rounded-lg" data-testid="text-bonus-conclusion">
                  {t('bonus.conclusion')}
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Why PriceForms Section */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4" data-testid="icon-why-priceforms">{t('whyPriceForms.icon')}</div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-why-priceforms">
                {t('whyPriceForms.title')}
              </h2>
              <p className="text-xl text-muted-foreground mb-8" data-testid="text-why-priceforms-subtitle">
                {t('whyPriceForms.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {Array.isArray(t('whyPriceForms.features', { returnObjects: true })) && (t('whyPriceForms.features', { returnObjects: true }) as any[]).map((feature: any, index: number) => {
                const Icon = whyPriceFormsIcons[index % whyPriceFormsIcons.length];
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`card-priceforms-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-2" data-testid={`title-priceforms-${index}`}>
                            {feature.title}
                          </h3>
                          <p className="text-muted-foreground" data-testid={`text-priceforms-${index}`}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <p className="text-center text-lg text-foreground font-medium" data-testid="text-why-priceforms-conclusion">
              {t('whyPriceForms.conclusion')}
            </p>
          </section>

          {/* FAQ Section */}
          <section className="mb-20">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4" data-testid="icon-faq">{t('faq.icon')}</div>
              <h2 className="text-3xl md:text-4xl font-bold mb-12" data-testid="title-faq">
                {t('faq.title')}
              </h2>
            </div>
            
            <div className="space-y-6">
              {Array.isArray(t('faq.questions', { returnObjects: true })) && (t('faq.questions', { returnObjects: true }) as any[]).map((item: any, index: number) => (
                <Card key={index} data-testid={`card-faq-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3" data-testid={`question-faq-${index}`}>
                      ❓ {item.question}
                    </h3>
                    <p className="text-muted-foreground text-lg" data-testid={`answer-faq-${index}`}>
                      {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="mb-16 bg-gradient-to-b from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <div className="text-6xl mb-6" data-testid="icon-cta">{t('cta.icon')}</div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="title-cta">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto font-medium" data-testid="text-cta-subtitle">
              {t('cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                onClick={handleCTAClick}
                data-testid="button-cta-primary"
              >
                {t('cta.primaryButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all bg-background"
                onClick={handleCTAClick}
                data-testid="button-cta-secondary"
              >
                {t('cta.secondaryButton')}
              </Button>
            </div>
          </section>
        </div>
      </div>
      
      <PageSpecificFAQStructuredData namespace="calculator-mistakes" />
      <Footer />
    </>
  );
}
